import { useEffect } from "react";
import { useLocation } from "wouter";
import SwipeableRow from "@/components/foundations/lists/SwipeableRow/SwipeableRow";
import { useTaskEditSheet } from "@/components/tasks/sheets/TaskEditSheet/TaskEditSheetHost";
import type { TaskEditSheetTask } from "@/components/tasks/sheets/TaskEditSheet/TaskEditSheet";
import { resolveTaskColors } from "@/lib/listColors";
import {
  getExecutionSwipeLabel,
  getSecondaryStatusSwipe,
} from "@shared/taskLifecycle";

type SwipeTask = TaskEditSheetTask;

type Props = {
  task: SwipeTask;
  children: React.ReactNode;
  disabled?: boolean;
  onSwipeRight: () => void;
  onSwipeLeft: () => void;
  onLongPress?: () => void;
  swipeRightLabel?: string;
  onAreaChange?: (listId: number | null) => void;
};

function isInteractiveTarget(target: EventTarget | null) {
  return target instanceof Element
    ? Boolean(target.closest("[data-sheet-ignore]"))
    : false;
}

export default function TaskSwipeActions({
  task,
  children,
  disabled = false,
  onSwipeRight,
  onSwipeLeft,
  onLongPress,
  swipeRightLabel,
}: Props) {
  const [location] = useLocation();
  const { openTask } = useTaskEditSheet();

  useEffect(() => {
    if (disabled) return;
    const params = new URLSearchParams(
      typeof window === "undefined" ? "" : window.location.search,
    );
    if (params.get("sheetTask") !== String(task.id)) return;
    openTask(task);
  }, [disabled, location, task.id, openTask]);

  const colors = resolveTaskColors({
    barColor: task.barColor ?? null,
    bgColor: task.bgColor ?? null,
    listColor: task.listColor ?? "#6366f1",
    listBgColor: task.listBgColor ?? null,
  });

  const effectiveSwipeRightLabel =
    swipeRightLabel ?? getExecutionSwipeLabel(task);
  const swipeLeftLabel = getSecondaryStatusSwipe(task).label;

  return (
    <SwipeableRow
      disabled={disabled}
      onSwipeRight={onSwipeRight}
      onSwipeLeft={onSwipeLeft}
      swipeRightLabel={effectiveSwipeRightLabel}
      swipeLeftLabel={swipeLeftLabel}
      swipeRightClassName=""
      swipeRightStyle={{
        background: colors.bar,
        color: colors.soft,
      }}
      swipeLeftClassName="bg-foreground text-background"
      onLongPress={onLongPress}
    >
      <div
        onClickCapture={e => {
          if (disabled) return;
          if (isInteractiveTarget(e.target)) return;
          e.preventDefault();
          e.stopPropagation();
          openTask(task);
        }}
      >
        {children}
      </div>
    </SwipeableRow>
  );
}
